import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Form, { Field as BaseField } from "rc-field-form";
import Field from "../../../../../../../../../components/Field";
import Select from "../../../../../../../../../components/Inputs/Select";
import ResourceSelect from "../../../../../../../../../components/Inputs/ResourceSelect";
import PayCodeSelect from "../../../PayCodeSelect";
import HourTypeSelect from "../../../../../../../../../components/Inputs/HourTypeSelect";
import Checkbox from "../../../../../../../../../components/Inputs/Checkbox";
import Button from "../../../../../../../../../components/Button";
import {
	renderSettingsResourceLabel,
	getSettingsResourceSearchFilters,
	getPathWithParams,
} from "../../../../../../../../../utils/helpers/settings";
import { payCodeCalcFields } from "../../../../../helper";
import { weekdays } from "../../../../../../../../../components/Inputs/WeekdaySelect";
import { firstToUpper } from "../../../../../../../../../utils/helpers/string";

function Details({ overtime, close, disabled }) {
	const { t } = useTranslation();
	const [hidden, setHidden] = useState(false);

	const onChange = useCallback(
		(e) => setHidden(!e.target.checked),
		[setHidden],
	);

	return (
		<Form
			name="all-hours-worked-on"
			onFinish={close}
			labelAlign="left"
			labelCol={{ span: 8 }}
			wrapperCol={{ span: 14, offset: 2 }}
			colon={false}
			initialValues={overtime}
			scrollToFirstError
		>
			<Field
				label={t("day")}
				name="value"
				rules={[
					{
						required: true,
						message: t("required-all-hours-worked-on"),
					},
				]}
			>
				<Select
					placeholder={firstToUpper(t("day"))}
					disabled={disabled}
					mode="multiple"
				>
					{weekdays.map((day) => (
						<Select.Option value={day}>{t(day)}</Select.Option>
					))}
				</Select>
			</Field>

			<Field
				label={t("default-pay-code")}
				name="defaultPayCode"
				rules={[
					{
						required: true,
						message: t("required-default-pay-code")
					}
				]}
			>
				<ResourceSelect
					labelPropName="description"
					renderLabel={renderSettingsResourceLabel}
					resourcePath={getPathWithParams('/pay-code', true, {}, payCodeCalcFields)}
					placeholder={t("default-pay-code")}
					hasSearch
					getSearchFilters={getSettingsResourceSearchFilters}
					disabled={disabled}
					allowClear
				/>
			</Field>

			<div className="mb-3">
				<BaseField name="splitOvertime" valuePropName="checked">
					<Checkbox
						id="splitOvertime"
						disabled={disabled}
					>
						{t("split-overtime")}
					</Checkbox>
				</BaseField>
			</div>

			<BaseField shouldUpdate>
				{({ }, { }, { getFieldValue }) => {
					const splitOvertime = getFieldValue("splitOvertime");

					return splitOvertime && (
						<Field
							label={t("premium-pay-code")}
							name="premiumPayCode"
							rules={[
								{
									required: true,
									message: t("required-premium-pay-code")
								}
							]}
						>
							<ResourceSelect
								labelPropName="description"
								renderLabel={renderSettingsResourceLabel}
								resourcePath={getPathWithParams('/pay-code', true, {}, payCodeCalcFields, {hourType: "unit"})}
								placeholder={t("premium-pay-code")}
								hasSearch
								getSearchFilters={getSettingsResourceSearchFilters}
								disabled={disabled}
								allowClear
							/>
						</Field>

					)
				}}
			</BaseField>

			<BaseField shouldUpdate noStyle>
				{({ }, { }, { getFieldValue, setFieldValue }) => {
					const payCodes = getFieldValue("payCodes");
					const onChange = (hourTypes) => {
						setFieldValue("payCodes", payCodes?.filter((payCode) =>
							hourTypes.includes(payCode.hourType),
						));
						setFieldValue("unitPayCode", undefined);
					};

					return (
						<Field
							label={t("eligible-hour-types")}
							name="hourTypes"
						>
							<HourTypeSelect
								mode="multiple"
								placeholder={t("eligible-hour-types")}
								onChange={onChange}
								disabled={disabled}
							/>
						</Field>
					);
				}}
			</BaseField>

			<BaseField shouldUpdate>
				{({ }, { }, { getFieldValue }) => {
					const hourTypes = getFieldValue("hourTypes");

					return hourTypes?.includes("unit") && (
						<Field
							label={t("unit-pay-code")}
							name="unitPayCode"
							rules={[
								{
									required: true,
									message: t("required-unit-pay-code")
								}
							]}
						>
							<ResourceSelect
								labelPropName="description"
								renderLabel={renderSettingsResourceLabel}
								resourcePath={getPathWithParams('/pay-code', true, {}, payCodeCalcFields, { hourType: "unit" })}
								placeholder={t("unit-pay-code")}
								hasSearch
								getSearchFilters={getSettingsResourceSearchFilters}
								disabled={disabled}
								allowClear
							/>
						</Field>

					)
				}}
			</BaseField>

			<div className="mb-3">
				<Checkbox
					checked={!hidden}
					onChange={onChange}
					id="show-pay-code"
					disabled={disabled}
				>
					{t("show-eligible-pay-code")}
				</Checkbox>
			</div>

			<BaseField shouldUpdate>
				{({ }, { }, { getFieldValue }) =>
					!hidden && (
						<PayCodeSelect
							hourTypes={getFieldValue("hourTypes")}
							name="payCodes"
							hidden={hidden}
							disabled={disabled}
						/>
					)
				}
			</BaseField>

			<div className="d-flex justify-content-between my-2">
				{disabled ? (
					<div />
				) : (
					<Button
						type="submit"
						color="primary"
						size="xs"
						className="bg-dark"
					>
						{t("save-changes")}
					</Button>
				)}

				<Button
					size="small"
					className="border-primary-color mr-1"
					onClick={close}
				>
					{t("close")}
				</Button>
			</div>
		</Form>
	);
}

export default Details;
